// Admin page for warehouse CRUD.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, X, Check, Trash2 } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { toast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";

interface Warehouse {
  id: string;
  name: string;
  address: string;
  isDefault: boolean;
  isActive: boolean;
}

export function WarehousesPage() {
  const qc = useQueryClient();
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [newName, setNewName] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [showNew, setShowNew] = useState(false);

  const { data: warehouses = [], isLoading } = useQuery({
    queryKey: ["warehouses"],
    queryFn: () =>
      apiClient.get<{ data: Warehouse[] }>("/v1/warehouses").then((r) => r.data.data),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: object }) =>
      apiClient.patch(`/v1/warehouses/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["warehouses"] });
      setEditId(null);
    },
  });

  const createMutation = useMutation({
    mutationFn: (body: object) => apiClient.post("/v1/warehouses", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["warehouses"] });
      setShowNew(false);
      setNewName("");
      setNewAddress("");
    },
  });

  function startEdit(w: Warehouse) {
    setEditId(w.id);
    setEditName(w.name);
    setEditAddress(w.address);
  }

  function saveEdit(id: string) {
    updateMutation.mutate({ id, body: { name: editName, address: editAddress } });
  }

  function toggleActive(w: Warehouse) {
    updateMutation.mutate({ id: w.id, body: { isActive: !w.isActive } });
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/v1/warehouses/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["warehouses"] }),
    onError: (err) => toast.error(translateApiError(err)),
  });

  function handleDelete(w: Warehouse) {
    if (window.confirm(`Xóa kho "${w.name}"? Chỉ xóa được kho chưa có tồn/đơn/phiếu.`)) {
      deleteMutation.mutate(w.id);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Kho hàng</h1>
        <Button size="sm" className="gap-1.5" onClick={() => setShowNew((v) => !v)}>
          <Plus size={16} /> Thêm kho
        </Button>
      </div>

      {showNew && (
        <div className="flex gap-2 max-w-xl rounded-md border p-3 bg-muted/20">
          <Input placeholder="Tên kho *" value={newName} onChange={(e) => setNewName(e.target.value)} />
          <Input placeholder="Địa chỉ" value={newAddress} onChange={(e) => setNewAddress(e.target.value)} />
          <Button
            size="sm"
            disabled={!newName.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate({ name: newName, address: newAddress })}
          >
            Lưu
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowNew(false)}><X size={16} /></Button>
        </div>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">Đang tải…</p>
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>Tên kho</Th>
              <Th>Địa chỉ</Th>
              <Th>Mặc định</Th>
              <Th>Trạng thái</Th>
              <Th />
            </Tr>
          </THead>
          <TBody>
            {warehouses.map((w) => (
              <Tr key={w.id}>
                <Td>
                  {editId === w.id ? (
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-7 text-sm" />
                  ) : (
                    w.name
                  )}
                </Td>
                <Td>
                  {editId === w.id ? (
                    <Input value={editAddress} onChange={(e) => setEditAddress(e.target.value)} className="h-7 text-sm" />
                  ) : (
                    w.address || "—"
                  )}
                </Td>
                <Td>{w.isDefault ? <span className="text-xs text-primary font-medium">Mặc định</span> : "—"}</Td>
                <Td>
                  <button
                    onClick={() => toggleActive(w)}
                    className={`rounded-full px-2 py-0.5 text-xs font-medium cursor-pointer ${
                      w.isActive ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {w.isActive ? "Hoạt động" : "Ẩn"}
                  </button>
                </Td>
                <Td>
                  {editId === w.id ? (
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => saveEdit(w.id)}>
                        <Check size={14} />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditId(null)}>
                        <X size={14} />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(w)}>
                        <Pencil size={14} />
                      </Button>
                      {!w.isDefault && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive"
                          title="Xóa kho"
                          disabled={deleteMutation.isPending}
                          onClick={() => handleDelete(w)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  )}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}
